import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { StudentFormOwnFieldDialog } from '@/components/studentForm/StudentFormOwnFieldDialog'
import { studentFormsApi, type StudentFormField } from '@/api/studentForms'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import '@/styles/dashboard.css'

/**
 * «Поля анкеты» в профиле учителя.
 *
 * Анкета одна и общая для всех его учеников: системные поля он настраивает
 * (убирает ненужные, делает необязательные обязательными), а недостающие
 * заводит сам.
 */
export function StudentFormFieldsPage() {
  const { t } = useTranslation()

  const [fields, setFields] = useState<StudentFormField[]>([])
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuField, setMenuField] = useState<StudentFormField | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<StudentFormField | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setFields(await studentFormsApi.getFields())
      setError(null)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('studentForms.loadError')))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuField(null)
  }

  const updateSetting = async (
    field: StudentFormField,
    payload: { hidden?: boolean; required?: boolean },
  ) => {
    setSavingKey(field.key)
    setError(null)
    try {
      setFields(await studentFormsApi.updateFieldSetting(field.key, payload))
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('studentForms.saveError')))
    } finally {
      setSavingKey(null)
    }
  }

  const handleDelete = async (field: StudentFormField) => {
    closeMenu()
    if (!window.confirm(t('studentForms.deleteOwnFieldConfirm'))) return
    try {
      setFields(await studentFormsApi.deleteOwnField(field.id))
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('studentForms.deleteError')))
    }
  }

  return (
    <ProfileLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('studentForms.fieldsTitle')}
          </Typography>
          <PageHelpPopover pageKey="profile-student-form-fields" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
            {t('studentForms.fieldsHint')}
          </Typography>
          <Button
            size="small"
            variant="contained"
            sx={{ height: 40 }}
            onClick={() => {
              setEditing(null)
              setDialogOpen(true)
            }}
          >
            {t('studentForms.createOwnField')}
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('studentForms.fieldLabel')}</TableCell>
                      <TableCell>{t('studentForms.fieldType')}</TableCell>
                      <TableCell>{t('studentForms.fieldAccess')}</TableCell>
                      <TableCell align="center">{t('studentForms.inForm')}</TableCell>
                      <TableCell align="center">{t('studentForms.fieldRequired')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {t('studentForms.noFields')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      fields.map((field) => (
                        <TableRow key={field.key} hover>
                          <TableCell>
                            {field.label}
                            {field.is_own ? (
                              <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ ml: 1 }}
                                label={t('studentForms.ownField')}
                              />
                            ) : (
                              !field.can_hide && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                  label={t('studentForms.fieldNotDeletable')}
                                />
                              )
                            )}
                          </TableCell>
                          <TableCell>{t(`studentForms.fieldTypes.${field.type}`)}</TableCell>
                          <TableCell>{t(`studentForms.accessLevels.${field.access}`)}</TableCell>
                          <TableCell align="center">
                            <Tooltip
                              title={field.can_hide ? '' : t('studentForms.cannotHideHint')}
                              disableHoverListener={field.can_hide}
                            >
                              <span>
                                <Switch
                                  size="small"
                                  checked={!field.hidden}
                                  disabled={!field.can_hide || savingKey === field.key}
                                  onChange={(e) =>
                                    void updateSetting(field, { hidden: !e.target.checked })
                                  }
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip
                              title={
                                field.can_toggle_required ? '' : t('studentForms.alwaysRequiredHint')
                              }
                              disableHoverListener={field.can_toggle_required}
                            >
                              <span>
                                <Switch
                                  size="small"
                                  checked={field.required}
                                  disabled={
                                    !field.can_toggle_required ||
                                    field.hidden ||
                                    savingKey === field.key
                                  }
                                  onChange={(e) =>
                                    void updateSetting(field, { required: e.target.checked })
                                  }
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            {/* Системные поля правит администратор — учителю менять там нечего */}
                            {field.is_own && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setMenuAnchor(e.currentTarget)
                                  setMenuField(field)
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
          <MenuItem
            onClick={() => {
              setEditing(menuField)
              setDialogOpen(true)
              closeMenu()
            }}
          >
            {t('common.edit', 'Редактировать')}
          </MenuItem>
          <MenuItem
            onClick={() => menuField && void handleDelete(menuField)}
            sx={{ color: 'error.main' }}
          >
            {t('common.delete', 'Удалить')}
          </MenuItem>
        </Menu>

        <StudentFormOwnFieldDialog
          open={dialogOpen}
          field={editing}
          onClose={() => setDialogOpen(false)}
          onSaved={(next) => {
            setDialogOpen(false)
            setFields(next)
          }}
        />
      </Box>
    </ProfileLayout>
  )
}
